/**
 * Admin-managed content from Supabase (public, RLS-restricted reads only).
 * When the backend is not configured, callers keep the static content in
 * src/data/*. Mapping keeps the exact shapes the components already use, so
 * the approved design does not change.
 */
import { backend, isBackendConfigured, publicHeaders } from "./config";

// Only columns granted to the anon role (never select=*).
const EVENT_COLUMNS = [
  "id", "slug", "title", "presented_by", "partners", "summary", "description", "event_date", "start_time",
  "status", "is_featured", "menu", "drinks", "entertainment", "poster_path", "poster_alt", "poster_headline",
  "poster_tagline", "portrait_path", "portrait_alt", "portrait_credit", "portrait_license", "price_cents",
  "currency", "price_basis", "price_note",
].join(",");

const headers = publicHeaders;

async function rest(path) {
  const res = await fetch(`${backend.url}/rest/v1/${path}`, { headers: headers() });
  if (!res.ok) throw new Error(`content ${res.status}`);
  return res.json();
}

export const storageUrl = (bucket, path) =>
  path ? `${backend.url}/storage/v1/object/public/${bucket}/${path.split("/").map(encodeURIComponent).join("/")}` : null;

/** "19:00:00" → "7:00 PM" (the format used across the site). */
export function formatStartTime(t) {
  if (!t) return null;
  const [h, m] = String(t).split(":").map(Number);
  const suffix = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${suffix}`;
}

/** Database event (+ performers) → the static event shape used by components. */
export function mapEvent(row, performers = [], base = {}) {
  return {
    ...base,
    slug: row.slug,
    title: row.title,
    date: row.event_date,
    startTime: formatStartTime(row.start_time),
    featured: row.is_featured,
    partners: row.partners?.length ? row.partners : base.partners,
    presentedBy: row.presented_by || base.presentedBy,
    summary: row.summary ?? base.summary,
    description: row.description ?? base.description,
    price: {
      amount: row.price_cents != null ? row.price_cents / 100 : base.price?.amount,
      currency: row.currency || base.price?.currency || "EUR",
      basis: row.price_basis || base.price?.basis,
      note: row.price_note || base.price?.note,
    },
    menu: row.menu || base.menu,
    drinks: row.drinks || base.drinks,
    poster: {
      ...(base.poster || {}),
      image: row.poster_path ? { src: storageUrl("event-media", row.poster_path), alt: row.poster_alt || `${row.title} poster` } : null,
      headline: row.poster_headline ?? base.poster?.headline,
      tagline: row.poster_tagline ?? base.poster?.tagline,
      portrait: {
        src: storageUrl("event-media", row.portrait_path),
        alt: row.portrait_alt || base.poster?.portrait?.alt,
        credit: row.portrait_credit,
        license: row.portrait_license,
      },
    },
    entertainment: {
      highlights: row.entertainment?.highlights || base.entertainment?.highlights || [],
      performers: performers.map((p) => ({
        id: p.id,
        name: p.name,
        description: p.description,
        time: p.performance_time,
        photo: p.photo_path ? { src: storageUrl("event-media", p.photo_path), alt: p.photo_alt || p.name } : null,
        sortOrder: p.sort_order,
      })),
    },
  };
}

export async function fetchEventContent(slug, base) {
  if (!isBackendConfigured()) return null;
  const rows = await rest(`events?select=${EVENT_COLUMNS}&slug=eq.${encodeURIComponent(slug)}&status=eq.published&limit=1`);
  if (!rows.length) return null;
  const performers = await rest(
    `event_performers?select=id,name,description,photo_path,photo_alt,performance_time,sort_order&event_id=eq.${rows[0].id}&is_published=eq.true&order=sort_order`
  );
  return mapEvent(rows[0], performers, base);
}

/** Menu: categories in admin order + published dishes, mapped to src/data/menu.js shapes. */
export async function fetchMenuContent() {
  if (!isBackendConfigured()) return null;
  const [cats, items] = await Promise.all([
    rest("menu_categories?select=id,slug,name,sort_order&is_visible=eq.true&order=sort_order"),
    rest("menu_items?select=id,category_id,name,description,price_cents,image_path,image_alt,allergens,is_available,featured_label,sort_order&is_published=eq.true&order=sort_order"),
  ]);
  const slugById = Object.fromEntries(cats.map((c) => [c.id, c.slug]));
  return {
    categories: cats.map((c) => ({ slug: c.slug, name: c.name })),
    items: items
      .filter((i) => slugById[i.category_id])
      .map((i) => ({
        id: i.id,
        name: i.name,
        description: i.description,
        price: i.price_cents != null ? i.price_cents / 100 : null,
        category: slugById[i.category_id],
        image: i.image_path ? { src: storageUrl("menu-images", i.image_path), alt: i.image_alt || i.name } : null,
        allergens: i.allergens || [],
        available: i.is_available,
        featuredLabel: i.featured_label,
        sortOrder: i.sort_order,
      })),
  };
}
